// Static category and location options for proposal creation.
// These are used by the create-proposal form instead of fetching from API routes.

export interface CategoryOption {
  id: string;
  name: string;
  emoji: string;
}

export interface LocationOption {
  id: string;
  name: string;
  emoji: string;
}

// Opinion-market discovery and creation order.
export const PROPOSAL_CATEGORIES: CategoryOption[] = [
  { id: 'culture', name: 'Culture', emoji: '\u{1F3AD}' },
  { id: 'sports', name: 'Sports', emoji: '\u{26BD}' },
  { id: 'gaming', name: 'Gaming', emoji: '\u{1F3AE}' },
  { id: 'crypto', name: 'Crypto', emoji: '\u{1FA99}' },
  { id: 'tech', name: 'Tech', emoji: '\u{1F4BB}' },
  { id: 'politics', name: 'Politics', emoji: '\u{1F3DB}\u{FE0F}' },
  { id: 'business', name: 'Business', emoji: '\u{1F4BC}' },
  { id: 'lifestyle', name: 'Lifestyle', emoji: '\u{1F33F}' },
];

export const DISCOVERY_CATEGORIES: readonly CategoryOption[] = PROPOSAL_CATEGORIES;

// Legacy categories remain in browser-saved drafts and onboarding progress.
const LEGACY_CATEGORY_IDS: ReadonlyMap<string, string> = new Map([
  ['entertainment', 'culture'],
  ['music', 'culture'],
  ['memes', 'culture'],
  ['technology', 'tech'],
  ['science', 'tech'],
  ['finance', 'business'],
  ['economics', 'business'],
  ['law', 'politics'],
  ['world', 'politics'],
  ['environment', 'politics'],
  ['food', 'lifestyle'],
  ['health', 'lifestyle'],
]);

export const PROPOSAL_LOCATIONS: LocationOption[] = [
  { id: 'global', name: 'Global', emoji: '\u{1F310}' },
  { id: 'ar', name: 'Argentina', emoji: '\u{1F1E6}\u{1F1F7}' },
  { id: 'au', name: 'Australia', emoji: '\u{1F1E6}\u{1F1FA}' },
  { id: 'br', name: 'Brazil', emoji: '\u{1F1E7}\u{1F1F7}' },
  { id: 'ca', name: 'Canada', emoji: '\u{1F1E8}\u{1F1E6}' },
  { id: 'ch', name: 'Switzerland', emoji: '\u{1F1E8}\u{1F1ED}' },
  { id: 'cn', name: 'China', emoji: '\u{1F1E8}\u{1F1F3}' },
  { id: 'co', name: 'Colombia', emoji: '\u{1F1E8}\u{1F1F4}' },
  { id: 'de', name: 'Germany', emoji: '\u{1F1E9}\u{1F1EA}' },
  { id: 'eg', name: 'Egypt', emoji: '\u{1F1EA}\u{1F1EC}' },
  { id: 'es', name: 'Spain', emoji: '\u{1F1EA}\u{1F1F8}' },
  { id: 'fr', name: 'France', emoji: '\u{1F1EB}\u{1F1F7}' },
  { id: 'gb', name: 'United Kingdom', emoji: '\u{1F1EC}\u{1F1E7}' },
  { id: 'gh', name: 'Ghana', emoji: '\u{1F1EC}\u{1F1ED}' },
  { id: 'id', name: 'Indonesia', emoji: '\u{1F1EE}\u{1F1E9}' },
  { id: 'in', name: 'India', emoji: '\u{1F1EE}\u{1F1F3}' },
  { id: 'it', name: 'Italy', emoji: '\u{1F1EE}\u{1F1F9}' },
  { id: 'jp', name: 'Japan', emoji: '\u{1F1EF}\u{1F1F5}' },
  { id: 'ke', name: 'Kenya', emoji: '\u{1F1F0}\u{1F1EA}' },
  { id: 'kr', name: 'South Korea', emoji: '\u{1F1F0}\u{1F1F7}' },
  { id: 'mx', name: 'Mexico', emoji: '\u{1F1F2}\u{1F1FD}' },
  { id: 'ng', name: 'Nigeria', emoji: '\u{1F1F3}\u{1F1EC}' },
  { id: 'nl', name: 'Netherlands', emoji: '\u{1F1F3}\u{1F1F1}' },
  { id: 'ph', name: 'Philippines', emoji: '\u{1F1F5}\u{1F1ED}' },
  { id: 'pk', name: 'Pakistan', emoji: '\u{1F1F5}\u{1F1F0}' },
  { id: 'pl', name: 'Poland', emoji: '\u{1F1F5}\u{1F1F1}' },
  { id: 'ru', name: 'Russia', emoji: '\u{1F1F7}\u{1F1FA}' },
  { id: 'sa', name: 'Saudi Arabia', emoji: '\u{1F1F8}\u{1F1E6}' },
  { id: 'se', name: 'Sweden', emoji: '\u{1F1F8}\u{1F1EA}' },
  { id: 'sg', name: 'Singapore', emoji: '\u{1F1F8}\u{1F1EC}' },
  { id: 'th', name: 'Thailand', emoji: '\u{1F1F9}\u{1F1ED}' },
  { id: 'tr', name: 'Turkey', emoji: '\u{1F1F9}\u{1F1F7}' },
  { id: 'ae', name: 'United Arab Emirates', emoji: '\u{1F1E6}\u{1F1EA}' },
  { id: 'us', name: 'United States', emoji: '\u{1F1FA}\u{1F1F8}' },
  { id: 'vn', name: 'Vietnam', emoji: '\u{1F1FB}\u{1F1F3}' },
  { id: 'za', name: 'South Africa', emoji: '\u{1F1FF}\u{1F1E6}' },
];

/** Map location id or display name -> flag emoji for use in list/detail. */
const LOCATION_FLAG_BY_ID = Object.fromEntries(
  PROPOSAL_LOCATIONS.map((loc) => [loc.id.toLowerCase(), loc.emoji]),
) as Record<string, string>;
const LOCATION_FLAG_BY_NAME = Object.fromEntries(
  PROPOSAL_LOCATIONS.map((loc) => [loc.name.toLowerCase(), loc.emoji]),
) as Record<string, string>;

/** Display strings that appear in fixtures or API but are not our canonical id/name. */
const LOCATION_FLAG_OVERRIDES: Record<string, string> = {
  'london, uk': PROPOSAL_LOCATIONS.find((l) => l.id === 'gb')?.emoji ?? '\u{1F1EC}\u{1F1E7}',
};

/**
 * Returns the flag emoji for a location string (id, label, or fixture display value), or null.
 */
export function getLocationFlag(location: string): string | null {
  const trimmed = location.trim();
  if (trimmed.length === 0) return null;
  const key = trimmed.toLowerCase();
  return (
    LOCATION_FLAG_BY_ID[key] ?? LOCATION_FLAG_BY_NAME[key] ?? LOCATION_FLAG_OVERRIDES[key] ?? null
  );
}

/**
 * Convert a category value (display name OR id) to the canonical lowercase id
 * the backend AI routes expect (`z.enum(CATEGORIES)` of slug ids). Browser-saved
 * forms can also retain retired category values; migrate those at the boundary.
 */
export function categoryNameToId(value: string): string {
  const v = value.trim();
  if (v.length === 0) return v;
  const byId = PROPOSAL_CATEGORIES.find((c) => c.id.toLowerCase() === v.toLowerCase());
  if (byId !== undefined) return byId.id;
  const byName = PROPOSAL_CATEGORIES.find((c) => c.name.toLowerCase() === v.toLowerCase());
  return byName?.id ?? LEGACY_CATEGORY_IDS.get(v.toLowerCase()) ?? v.toLowerCase();
}

/**
 * Convert a location value (display name OR id) to the canonical lowercase id
 * the backend stores on `market.location` / `proposal.location`. The region
 * multi-select holds the display name (`"United States"`), but the indexer
 * stores the slug (`"us"`) — call this at the wire boundary before filtering,
 * mirroring {@link categoryNameToId}.
 */
export function locationNameToId(value: string): string {
  const v = value.trim();
  if (v.length === 0) return v;
  const byId = PROPOSAL_LOCATIONS.find((l) => l.id.toLowerCase() === v.toLowerCase());
  if (byId !== undefined) return byId.id;
  const byName = PROPOSAL_LOCATIONS.find((l) => l.name.toLowerCase() === v.toLowerCase());
  return byName?.id ?? v.toLowerCase();
}
